<template>
  <v-card
    flat
    class="ma-3 pa-6"
  >
    <v-row align="center" class="pl-0 mb-6 pt-3 pr-3">
      <v-card-title>
        Decorations
      </v-card-title>
      <v-spacer></v-spacer>
      <v-text-field
        outlined
        dense
        v-model="filter.query"
        placeholder="Search..."
        append-icon="fas fa-search"
        clearable
        clear-icon="fas fa-times"
        single-line
        hide-details
        @keyup="searchKeyUp()"
        @click:clear="searchKeyUp()"
      ></v-text-field>
      <v-btn
        class="ml-4"
        depressed
        color="secondary"
        @click="newClick"
        :disabled="!user.can('decoration.create')"
      >
    <v-icon
      small
      left
    >fas fa-folder-plus</v-icon>
    New
    </v-btn>
    </v-row>

    <v-data-table
      :class="{ 'clickable': user.can('decoration.update') }"
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalCount"
      :footer-props="{ 'items-per-page-options': [5, 10, 50] }"
      :loading="isSearching"
      @click:row="rowClick"
    >

      <template v-slot:item.width="{ value }">
        {{ value }} mm
      </template>

      <template v-slot:item.height="{ value }">
        {{ value }} mm
      </template>

    </v-data-table>


    <v-dialog
      v-model="dialog"
      max-width="700"
      persistent
    >
      <v-card>
        <v-toolbar color="primary" dark flat short class="px-2">
          <v-toolbar-title>Decoration</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn @click="dialog = false" icon><v-icon>fas fa-times</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="mt-12">
          <v-form @submit.prevent>

            <v-autocomplete
              outlined
              clearable
              clear-icon="fas fa-times"
              class="mb-2"
              v-model="model.technique_id"
              :error-messages="errors.technique_id"
              item-text="description"
              item-value="id"
              :items="techniques"
              label="Technique"
            ></v-autocomplete>

            <v-text-field
              outlined
              class="mb-2"
              v-model="model.description"
              :error-messages="errors.description"
              label="Description"
            ></v-text-field>

            <v-text-field
              outlined
              class="mb-2"
              v-model="model.slug"
              :error-messages="errors.slug"
              label="Slug"
            ></v-text-field>

            <number-field
              outlined
              class="mb-2"
              v-model="model.width"
              :error-messages="errors.width"
              label="Width"
              decimals="0"
              decimal-separator="."
              suffix="mm"
            ></number-field>

            <number-field
              outlined
              class="mb-2"
              v-model="model.height"
              :error-messages="errors.height"
              label="Height"
              decimals="0"
              decimal-separator="."
              suffix="mm"
            ></number-field>


          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-8">
          <v-btn
            v-if="model.id"
            color="error"
            class=""
            large
            outlined
            depressed
            @click="$refs.deleteItemConfirmDialog.show()"
          >Delete</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="px-5"
            large
            outlined
            depressed
            @click="dialog = false"
          >Cancel</v-btn>
          <v-btn
            color="primary"
            class="px-5"
            large
            depressed
            @click="saveClick()"
          >Save</v-btn>
        </v-card-actions>

        <ConfirmDialog
          ref="deleteItemConfirmDialog"
          @confirm="deleteItem()"
          title="Confirm"
          message="Decoration will be deleted. Are you sure?"
          labelConfirm="OK"
          labelCancel="Cancel"
        ></ConfirmDialog>

      </v-card>

    </v-dialog>

  </v-card>
  
</template>

<script>
import { inject, ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import NumberField from '@/components/NumberField'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

export default {
  components: {
    NumberField,
    ConfirmDialog,
  },
  setup() {
    const api = inject('api')
    const loader = inject('loader')
    const router = useRouter()
    const { user } = api

    const techniqueCollection = api.collection('techniques')
    const { items: techniques } = techniqueCollection.all()


    const itemCollection = api.collection('decorations', {
      params: {
        expand: 'technique',
      },
      filter: {
        query: 'query:description,slug',
      },
      store: true,
    })

    const { items, filter, totalCount, options, isSearching } = itemCollection

    const headers = [
      { text: 'Technique', value: 'technique.description', },
      { text: 'Description', value: 'description', },
      { text: 'Slug', value: 'slug' },
      { text: 'Width', value: 'width' },
      { text: 'Height', value: 'height' },
    ]

    const dialog = ref(false)

    const itemModel = api.model('decorations/:id', {
      params: {
      },
    })
    const { attributes: model, errors, isBusy } = itemModel

    loader.bindRef(isBusy)

    function newClick() {
      itemModel.clear().setAttributes({ status: 9 })
      dialog.value = true
    }

    function rowClick(item) {
      if (user.can('decoration.update') && item?.id) {
        router.push({
          name: 'decoration',
          params: {
            id: item.id
          }
        })
      }
    }

    function saveClick() {
      itemModel.save().then(() => {
        itemCollection.search()
        dialog.value = false
        router.push({
          name: 'decoration',
          params: {
            id: model?.value.id,
          }
        })
      })
    }

    function deleteItem() {
      itemModel.delete().then(() => {
        itemCollection.search()
        dialog.value = false
      })
    }

    const searchKeyUp = itemCollection.debounceSearch(300)

    return {
      user,
      headers,
      items,
      filter,
      searchKeyUp,
      totalCount,
      options,
      isSearching,
      newClick,
      rowClick,
      saveClick,
      deleteItem,
      dialog,
      model,
      errors,
      techniques,
    }

  },
}
</script>


<style scoped>

:deep(.v-data-table.clickable table tbody tr:not(.v-data-table__empty-wrapper):hover) {
  cursor: pointer;
}

</style>
